import { useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import {
  useDataProvider,
  useGetIdentity,
  useGetList,
  useNotify,
  useTranslate,
} from "ra-core";

import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Sheet,
  SheetContent,
  SheetFooter,
  SheetHeader,
  SheetTitle,
} from "@/components/ui/sheet";
import { Textarea } from "@/components/ui/textarea";
import { useIsMobile } from "@/hooks/use-mobile";
import { parseChecklistSteps } from "./checklistSteps";
import {
  addChecklistSteps,
  createTaskFromTemplate,
  type TemplateItem,
  useChecklistTemplates,
  dateInputToIso,
  fullName,
  isoToDateInput,
  nativeSelectClass,
  useTaskAreas,
  useTeam,
} from "./taskData";
import { DEFAULT_TASK_TYPE } from "./taskTypes";

interface TaskQuickCreateProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  defaultAreaId?: number;
}

// An internal task in one sheet: title, area, owner, due date, priority and
// the checklist pasted as lines (task 1.2: a Trello card in under a minute).
export const TaskQuickCreate = ({
  open,
  onOpenChange,
  defaultAreaId,
}: TaskQuickCreateProps) => {
  const translate = useTranslate();
  const notify = useNotify();
  const isMobile = useIsMobile();
  const dataProvider = useDataProvider();
  const queryClient = useQueryClient();
  const { identity } = useGetIdentity();
  const { data: areas = [] } = useTaskAreas();
  const { data: team = [] } = useTeam();
  const { data: templates = [] } = useChecklistTemplates();

  const [templateId, setTemplateId] = useState("");
  const { data: templateItems = [] } = useGetList<TemplateItem>(
    "checklist_template_items",
    {
      pagination: { page: 1, perPage: 500 },
      sort: { field: "position", order: "ASC" },
      filter: { template_id: Number(templateId) },
    },
    { enabled: !!templateId },
  );
  const [title, setTitle] = useState("");
  const [areaId, setAreaId] = useState<string>(
    defaultAreaId ? String(defaultAreaId) : "",
  );
  const [assigneeId, setAssigneeId] = useState<string>("");
  const [dueDate, setDueDate] = useState(
    isoToDateInput(new Date().toISOString()),
  );
  const [priority, setPriority] = useState("2");
  const [steps, setSteps] = useState("");
  const [isSaving, setIsSaving] = useState(false);

  // From a template (task 1.3): title, area and owner come from the
  // template, its steps are copied by the database; pasted steps are added
  // after them.
  const chooseTemplate = (value: string) => {
    const previous = templates.find((t) => String(t.id) === templateId);
    const next = templates.find((t) => String(t.id) === value);
    setTemplateId(value);
    if (!title.trim() || title === previous?.name) setTitle(next?.name ?? "");
    if (next?.area_id) setAreaId(String(next.area_id));
    if (next?.default_assignee_id) {
      setAssigneeId(String(next.default_assignee_id));
    }
  };

  const reset = () => {
    setTemplateId("");
    setTitle("");
    setSteps("");
    setPriority("2");
    setAssigneeId("");
    setDueDate(isoToDateInput(new Date().toISOString()));
  };

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault();
    if (!title.trim()) return;
    setIsSaving(true);
    try {
      const assignee = assigneeId ? Number(assigneeId) : (identity?.id ?? null);
      const task = templateId
        ? await createTaskFromTemplate({
            templateId: Number(templateId),
            title: title.trim(),
            assignee: assignee as number | null,
            dueDate: dateInputToIso(dueDate),
            priority: Number(priority),
            areaId: areaId ? Number(areaId) : null,
          })
        : (
            await dataProvider.create("tasks", {
              data: {
                text: title.trim(),
                scope: "internal",
                area_id: areaId ? Number(areaId) : null,
                sales_id: assignee,
                due_date: dateInputToIso(dueDate),
                priority: Number(priority),
                type: DEFAULT_TASK_TYPE,
              },
            })
          ).data;
      await addChecklistSteps(
        task.id,
        parseChecklistSteps(steps),
        (templateId ? templateItems.length : 0) + 1,
      );
      await queryClient.invalidateQueries({ queryKey: ["tasks"] });
      await queryClient.invalidateQueries({
        queryKey: ["task_checklist_items"],
      });
      notify("tenax.tasks.created", { type: "success" });
      reset();
      onOpenChange(false);
    } catch (error) {
      notify(error instanceof Error ? error.message : String(error), {
        type: "error",
      });
    } finally {
      setIsSaving(false);
    }
  };

  const activeAreas = areas.filter((area) => area.active);

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent
        side={isMobile ? "bottom" : "right"}
        className="max-h-[95dvh] overflow-y-auto"
      >
        <form onSubmit={handleSubmit} className="flex flex-col gap-4 p-4">
          <SheetHeader className="p-0">
            <SheetTitle>{translate("tenax.tasks.new")}</SheetTitle>
          </SheetHeader>

          {templates.some((template) => template.active) && (
            <div className="flex flex-col gap-1.5">
              <Label htmlFor="task-template">
                {translate("tenax.tasks.templates.from_template")}
              </Label>
              <select
                id="task-template"
                className={nativeSelectClass}
                value={templateId}
                onChange={(event) => chooseTemplate(event.target.value)}
              >
                <option value="">
                  {translate("tenax.tasks.templates.none")}
                </option>
                {templates
                  .filter((template) => template.active)
                  .map((template) => (
                    <option key={template.id} value={template.id}>
                      {template.name}
                    </option>
                  ))}
              </select>
              {templateId && (
                <p className="text-muted-foreground text-xs">
                  {templateItems.map((item) => item.label).join(" · ")}
                </p>
              )}
            </div>
          )}

          <div className="flex flex-col gap-1.5">
            <Label htmlFor="task-title">
              {translate("tenax.tasks.fields.title")}
            </Label>
            <Input
              id="task-title"
              autoFocus
              required
              value={title}
              onChange={(event) => setTitle(event.target.value)}
            />
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div className="flex flex-col gap-1.5">
              <Label htmlFor="task-area">
                {translate("tenax.tasks.fields.area")}
              </Label>
              <select
                id="task-area"
                className={nativeSelectClass}
                value={areaId}
                onChange={(event) => setAreaId(event.target.value)}
              >
                <option value="">
                  {translate("tenax.tasks.fields.no_area")}
                </option>
                {activeAreas.map((area) => (
                  <option key={area.id} value={area.id}>
                    {area.name}
                  </option>
                ))}
              </select>
            </div>
            <div className="flex flex-col gap-1.5">
              <Label htmlFor="task-assignee">
                {translate("tenax.tasks.fields.assignee")}
              </Label>
              <select
                id="task-assignee"
                className={nativeSelectClass}
                value={assigneeId || String(identity?.id ?? "")}
                onChange={(event) => setAssigneeId(event.target.value)}
              >
                {team.map((member) => (
                  <option key={member.id} value={member.id}>
                    {fullName(member)}
                  </option>
                ))}
              </select>
            </div>
            <div className="flex flex-col gap-1.5">
              <Label htmlFor="task-due">
                {translate("tenax.tasks.fields.due_date")}
              </Label>
              <Input
                id="task-due"
                type="date"
                value={dueDate}
                onChange={(event) => setDueDate(event.target.value)}
              />
            </div>
            <div className="flex flex-col gap-1.5">
              <Label htmlFor="task-priority">
                {translate("tenax.tasks.fields.priority")}
              </Label>
              <select
                id="task-priority"
                className={nativeSelectClass}
                value={priority}
                onChange={(event) => setPriority(event.target.value)}
              >
                {[1, 2, 3].map((level) => (
                  <option key={level} value={level}>
                    {translate(`tenax.tasks.priority.${level}`)}
                  </option>
                ))}
              </select>
            </div>
          </div>

          <div className="flex flex-col gap-1.5">
            <Label htmlFor="task-steps">
              {translate("tenax.tasks.fields.steps")}
            </Label>
            <Textarea
              id="task-steps"
              rows={5}
              value={steps}
              onChange={(event) => setSteps(event.target.value)}
            />
            <p className="text-muted-foreground text-xs">
              {translate("tenax.tasks.fields.steps_hint")}
            </p>
          </div>

          <SheetFooter className="p-0">
            <Button type="submit" disabled={isSaving || !title.trim()}>
              {translate("ra.action.save")}
            </Button>
          </SheetFooter>
        </form>
      </SheetContent>
    </Sheet>
  );
};
